
// A hook site for extensions. Drop it wherever customizations should be able to
// appear; it finds the extensions that registered for this type+location and
// match the current context, and renders them.
//
//   <ExtensionSlot type="contentList" location="toolbar" />
//
// A body is optional fallback content, rendered only while no extension matches -
// so a slot can also mean "this, unless someone customizes it":
//
//   <ExtensionSlot type="EntryEditor" location="sidebar" :state="sidebarState">
//       <TheDefaultSidebar />
//   </ExtensionSlot>
//
// The fallback lives in the host's template, so host CSS - scoped rules included -
// applies to it exactly as if the slot were not there.
//
// Most of the context is injected, so hook sites stay a single line. Only the
// slot-local part (the entry, field, control being rendered, plus any host state
// extensions need) is passed as props.
//
// ATT: this is a *functional* component that returns an array of vnodes, so the
//      slot contributes no element of its own. That is not cosmetic: host CSS
//      routinely uses child selectors (`.tabs > span`, `.bottom-toolbar > button`)
//      and any wrapper element would silently break the styling of everything
//      rendered through the slot. Being functional is also why the context is
//      read from `injections` rather than `this`.
import { getExtensions } from '@/lib/extensions/registry'

// One error boundary per extension: a broken extension logs and renders nothing
// instead of taking the whole slot - or the host - down with it. Its root element
// *is* the extension's root, so it adds no DOM either.
const ExtensionHost = {
	name: 'ExtensionHost',
	props: { ext: Object, ctx: Object },
	data: () => ({ failed: false }),
	errorCaptured(e) {
		console.error(`[extensions] ${this.ext.id} failed and was removed`, e)
		this.failed = true
		return false
	},
	render(h) {
		if (this.failed) return null
		return h(this.ext.component, { props: { ctx: this.ctx } })
	},
}

export default {
	functional: true,
	props: {
		type: { type: String, required: true },
		location: { type: String, required: true },
		// slot-local context - whatever the hook site happens to have
		entry: Object,
		contentType: [ Object, String ],
		field: Object,
		control: Object,
		locale: String,
		// host state (and callbacks into the host) that extensions in this slot
		// need, e.g. which sidebar tab is selected and how to select one. Reaches
		// extensions as ctx.state.
		state: Object,
	},
	// Defaults throughout: a slot must work in trees where a given provider is
	// absent (the type editor, compare views) without emitting injection warnings.
	inject: {
		spaceId: { default: null },
		environment: { default: null },
		space: { default: null },
		locales: { default: null },
		defaultLocale: { default: null },
		uiConfig: { default: null },
		permissions: { default: null },
		settings: { default: null },
		me: { default: null },
		tags: { default: null },
		eventBus: { default: null },
		base: { default: null },
		endpoint: { default: null },
	},
	render(h, { props, injections, slots }) {
		// The single contract extensions get. One object instead of a prop list
		// that grows per slot type, and the same object when() is filtered with.
		const ctx = {
			...injections,
			contentType: props.contentType,
			entry: props.entry,
			field: props.field,
			control: props.control,
			locale: props.locale,
			state: props.state ?? {},
		}

		const extensions = getExtensions(props.type, props.location, ctx)
		// nothing matched -> the host's own content, if it provided any
		if (!extensions.length) return slots().default
		return extensions.map(ext => h(ExtensionHost, { key: ext.id, props: { ext, ctx } }))
	},
}
